import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'admin-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** An explicit choice lapses after this long and the device decides again. Most staff
 * never touch the toggle; of those who do, many tapped it once by accident. Expiring the
 * override keeps a stray tap from outliving its usefulness, while a preference someone
 * keeps re-asserting is refreshed on every toggle and so never expires under them. */
const CHOICE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const ThemeContext = createContext<{ theme: Theme; toggleTheme: () => void }>({
  theme: 'light',
  toggleTheme: () => {},
});

/** The user's explicit, still-valid choice, or null when they have made none, it has
 * lapsed, or storage is unreadable. A bare string is the legacy format — it was written
 * automatically by the old mount effect rather than chosen, so it counts as lapsed.
 * `JSON.parse` rejects it for us. */
function readChoice(): Theme | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { theme, at } = parsed as { theme?: unknown; at?: unknown };
    if (theme !== 'light' && theme !== 'dark') return null;
    if (typeof at !== 'number' || Date.now() - at > CHOICE_TTL_MS) return null;
    return theme;
  } catch {
    // Legacy bare string, corrupt JSON, or storage blocked in private mode.
    return null;
  }
}

function writeChoice(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ theme, at: Date.now() }));
  } catch {
    // Storage blocked — the choice still applies for this page view.
  }
}

function devicePreference(): Theme {
  try {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
  } catch {
    // matchMedia unavailable or throwing — degrade to light, matching index.html's pre-paint script.
    return 'light';
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Resolved once, on load. There is deliberately no matchMedia listener: a tab someone
  // already has open must not change theme under them when the OS flips at sunset.
  const [theme, setTheme] = useState<Theme>(() => readChoice() ?? devicePreference());

  // Applies the theme. Deliberately does NOT write storage: persisting here would record
  // whatever the device happened to say on first load as though the user had chosen it,
  // pinning them to it. Only toggleTheme persists.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    // Keeps native scrollbars, form controls and browser chrome in step with the page.
    root.style.colorScheme = theme;
  }, [theme]);

  const toggleTheme = () =>
    setTheme(current => {
      const next: Theme = current === 'light' ? 'dark' : 'light';
      writeChoice(next);
      return next;
    });

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => useContext(ThemeContext);
